import { Button, ButtonGroup, Modal } from "@datapay-by-datacom/design-system";
import { Heading } from "react-aria-components";
import styles from "./MatchingImportConfirmModal.module.css";

/** Confirm modes for replace / commit / override. Cancel uses its own modal. */
export type MatchingConfirmMode = "replace" | "commit" | "override" | null;

export interface MatchingImportConfirmModalProps {
  mode: MatchingConfirmMode;
  rowCount: number;
  warningCount: number;
  /** Rows with a blocking validation error — withheld from the commit (partial import). */
  skippedCount?: number;
  /** Eligible DELETE_KEEP_AMOUNT rows (override off, stored amount kept). */
  keptOverrideCount?: number;
  /** Eligible DELETE rows (override off, amount cleared). */
  clearedOverrideCount?: number;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirms replace / commit / override before matching rows are written.
 * Commit and override use the same at-a-glance summary as Work Patterns
 * (#401): green import count, optional red skipped count, optional amber
 * warning count — no plain "N warnings remain" prose above the list.
 */
export function MatchingImportConfirmModal({
  mode,
  rowCount,
  warningCount,
  skippedCount = 0,
  keptOverrideCount = 0,
  clearedOverrideCount = 0,
  onConfirm,
  onCancel,
}: MatchingImportConfirmModalProps) {
  const isOpen = mode !== null;
  const importCount = Math.max(rowCount - skippedCount, 0);
  const isPartial = skippedCount > 0;
  const overrideWarnings = mode === "override";
  const isCommitFlow = mode === "commit" || mode === "override";

  const importLabel = importCount === 1 ? "row" : "rows";
  const skippedLabel = skippedCount === 1 ? "row" : "rows";
  const warningLabel = warningCount === 1 ? "warning" : "warnings";

  let title: string;
  let confirmLabel: string;

  if (mode === "replace") {
    title = "Replace uploaded file?";
    confirmLabel = "Replace file";
  } else if (isPartial && overrideWarnings) {
    title = "Import valid rows and override warnings?";
    confirmLabel = "Override and import";
  } else if (isPartial) {
    title = "Import valid rows?";
    confirmLabel = "Import valid rows";
  } else if (overrideWarnings) {
    title = "Override warnings and import?";
    confirmLabel = "Override and import";
  } else {
    title = "Confirm import?";
    confirmLabel = "Confirm import";
  }

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <div className={styles.modalContent}>
        <Heading slot="title" className={styles.title}>
          {title}
        </Heading>

        {mode === "replace" && (
          <p className={styles.message}>
            Replacing the file clears the current batch, edits, and validation
            results.
          </p>
        )}

        {isCommitFlow && (
          <ul className={styles.summary}>
            <li className={`${styles.summaryItem} ${styles.itemSuccess}`}>
              <span className={styles.itemIcon} aria-hidden="true">
                ✓
              </span>
              <span className={styles.itemCount}>{importCount}</span>
              <span className={styles.itemLabel}>
                valid {importLabel} will be imported
              </span>
            </li>

            {keptOverrideCount > 0 && (
              <li className={`${styles.summaryItem} ${styles.itemSuccess}`}>
                <span className={styles.itemIcon} aria-hidden="true">
                  ✓
                </span>
                <span className={styles.itemCount}>{keptOverrideCount}</span>
                <span className={styles.itemLabel}>
                  {keptOverrideCount === 1 ? "override" : "overrides"} turned
                  off (amount kept)
                </span>
              </li>
            )}

            {clearedOverrideCount > 0 && (
              <li className={`${styles.summaryItem} ${styles.itemSuccess}`}>
                <span className={styles.itemIcon} aria-hidden="true">
                  ✓
                </span>
                <span className={styles.itemCount}>{clearedOverrideCount}</span>
                <span className={styles.itemLabel}>
                  {clearedOverrideCount === 1 ? "override" : "overrides"}{" "}
                  cleared
                </span>
              </li>
            )}

            {isPartial && (
              <li className={`${styles.summaryItem} ${styles.itemDanger}`}>
                <span className={styles.itemIcon} aria-hidden="true">
                  ✕
                </span>
                <span className={styles.itemCount}>{skippedCount}</span>
                <span className={styles.itemLabel}>
                  {skippedLabel} skipped — download the issues CSV, fix, and
                  re-upload
                </span>
              </li>
            )}

            {overrideWarnings && warningCount > 0 && (
              <li className={`${styles.summaryItem} ${styles.itemWarning}`}>
                <span className={styles.itemIcon} aria-hidden="true">
                  ⚠
                </span>
                <span className={styles.itemCount}>{warningCount}</span>
                <span className={styles.itemLabel}>
                  kept {warningLabel} — check each warning before confirming;
                  eligible rows still import with your CSV values
                </span>
              </li>
            )}
          </ul>
        )}

        <div className={styles.actions}>
          <ButtonGroup>
            <Button variant="tertiary" onPress={onCancel}>
              Keep working
            </Button>
            <Button variant="primary" onPress={onConfirm}>
              {confirmLabel}
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </Modal>
  );
}
